import { toRaw } from 'vue';
import { createLogicGraph } from './logicGraph.js';
import { migrateHandlersToLogic } from './logicMigration.js';
import { LOGIC_PLACEMENT } from './logicPlacement.js';

const isPageObject = (page, node) => node !== page && typeof node?.obj === 'string' &&
    node.haspid !== undefined && node.haspid !== null && typeof node.id === 'function';

/**
 * Move stored per-object handlers (`node.actions`, from an older design, a
 * device JSONL import or an older custom component) into the page's logic
 * graph, then clear them. Event nodes go in a column right of the plate,
 * level with their object. Idempotent: objects without handlers are skipped.
 *
 * Kept apart from pageLogic.js so HaspPage does not import the node factory
 * (and through it the whole action module graph).
 *
 * @returns {boolean} whether anything was migrated
 */
export function migratePageActions(page, { plateWidth = 0 } = {}) {
    page = toRaw(page);
    const objects = (page?.find?.(node => isPageObject(page, node)) ?? [])
        .filter(node => Array.isArray(node.actions) && node.actions.length > 0);
    if (objects.length === 0) return false;
    const toPage = page.getAbsoluteTransform?.().copy().invert();
    page.logic = migrateHandlersToLogic(objects.map(node => ({
        uuid: node.id(),
        handlers: node.actions,
        y: toPage ? toPage.point(node.getAbsolutePosition()).y : (node.y?.() ?? 0),
    })), { graph: page.logic ?? createLogicGraph(), originX: plateWidth + LOGIC_PLACEMENT.plateGap });
    objects.forEach(node => { node.actions = []; });
    return true;
}

export function migrateLayerActions(layer) {
    const raw = toRaw(layer);
    return (raw?.getChildren?.() ?? [])
        .filter(page => page?.obj === 'page' || page?.obj === 'template_page')
        .map(page => migratePageActions(page, { plateWidth: raw.pageWidth ?? 0 }))
        .some(Boolean);
}
